import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  activeSvg,
  addTagSvg,
  deleteTagSvg,
  dollarSvg,
  editSvg,
  inactiveSvg,
  settingSvg,
  trashSvg,
} from "../assets/icons";
import { showConfirmToast } from "../components/ConfirmToast";
import { EditUserModal } from "../components/EditUserModal";
import {
  deleteTicket,
  getTickets,
  getTournamentTicketCategories,
  updateTicket,
} from "../data/httpClient";
import { useTournamentStateContext } from "../data/providers/TournamentStateProvider";
import { Ticket, TicketCategory } from "../types";
import { makeAccessibleClick } from "../utils/makeAccessibleClick";
import { notify } from "../utils/notify";

export const TicketsPage = () => {
  const navigate = useNavigate();

  const { tournamentId, attendeeOrSponsor } = useParams();
  const [editUserModal, setEditUserModal] = useState(false);
  const [editUserId, setEditUserId] = useState<string | number>(0);

  const openEditUserModal = (userId: string | number) => {
    setEditUserId(userId);
    setEditUserModal(true);
  };
  const { setTournamentIdState } = useTournamentStateContext();

  const [filterTags, setFilterTags] = useState([
    {
      name: "Paid",
      selected: false,
    },
    {
      name: "Unpaid",
      selected: false,
    },
    {
      name: "Cancelled",
      selected: false,
    },
    {
      name: "Not Cancelled",
      selected: false,
    },
  ]);

  // ticketCategories decides how many tickets tables we list
  const [tabs, setTabs] = useState<{ name: string; selected: boolean }[]>([]);

  const [tickets, setTickets] = useState<Ticket[]>([]);

  const isSponsor = attendeeOrSponsor === "sponsor" ? true : false;

  const getTabs = async () => {
    if (tournamentId) {
      setTournamentIdState(parseInt(tournamentId));
      const ticketCategories = await getTournamentTicketCategories(
        parseInt(tournamentId),
        isSponsor
      );
      setTabs(
        ticketCategories.map((t: TicketCategory, i: number) => {
          return {
            name: t.name,
            selected: i === 0 ? true : false,
          };
        })
      );
    }
  };

  const getTicketsResult = async () => {
    if (tournamentId) {
      let result = await getTickets(parseInt(tournamentId), isSponsor);
      const selectedTabObj = tabs.find(
        (t: { name: string; selected: boolean }) => t.selected
      );
      result = result.filter(
        (t: Ticket) => t.ticketCategory?.name === selectedTabObj?.name
      );
      setTickets(result);
      filterTags
        .filter((f) => f.selected)
        .forEach((f) => {
          result = result.filter((t: Ticket) => {
            switch (f.name) {
              case "Paid":
                return t.paid;
              case "Unpaid":
                return !t.paid;
              case "Cancelled":
                return t.isCancelled;
              case "Not Cancelled":
                return !t.isCancelled;
              default:
                return true;
            }
          });
        });
      setTickets(result);
    }
  };

  useEffect(() => {
    getTabs();
  }, [tournamentId, attendeeOrSponsor]);

  useEffect(() => {
    getTicketsResult();
  }, [tournamentId, attendeeOrSponsor, tabs, filterTags]);

  return (
    <>
      <EditUserModal
        isOpen={editUserModal}
        onRequestClose={() => setEditUserModal(false)}
        userId={editUserId}
        onUpdate={getTicketsResult}
      />
      <div className="ticketsPageWrapper adminTicketsPage">
        <h5 className="ticketsPageTitle">
          <span>{attendeeOrSponsor}</span> Tickets{" "}
        </h5>

        <button
          className="newTicketBtn"
          type="button"
          onClick={() => navigate("new")}
        >
          New Ticket
        </button>

        <article className="filterWrapper">
          {filterTags.map((f, i) => {
            return (
              <button
                key={`ticket-filterTag-${i}`}
                className={`filterTag ${f.selected && "tagSelected"}`}
                onClick={() => {
                  filterTags[i].selected = !filterTags[i].selected;
                  setFilterTags([...filterTags]);
                }}
              >
                <span>{f.name}</span>
                {f.selected ? (
                  <img src={deleteTagSvg} alt="unselected filter tag" />
                ) : (
                  <img src={addTagSvg} alt="selected filter tag" />
                )}
              </button>
            );
          })}
        </article>

        {
          <section className="ticketTabWrapper">
            {tabs.map((t: { name: string; selected: boolean }, i: number) => (
              <button
                key={`tickets-tab-${i}`}
                className={`userTabBtn ${t.selected && "selected"}`}
                onClick={() => {
                  tabs.forEach((t) => (t.selected = false));
                  tabs[i].selected = true;
                  setTabs([...tabs]);
                }}
              >
                {t.name}
              </button>
            ))}
          </section>
        }

        <table className="ticketsTable">
          <thead>
            <tr>
              <th>Ticket Holder</th>
              {attendeeOrSponsor === "sponsor" && <th>Sponsor</th>}
              {attendeeOrSponsor === "sponsor" && <th>Contact</th>}
              <th>Groups</th>
              <th>Payment</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {!tickets.length ? (
              <tr>
                <td>No results</td>
              </tr>
            ) : (
              tickets.map((t: Ticket) => {
                return (
                  <tr key={t.id}>
                    <td>
                      {t.user ? (
                        <span
                          className="editableUserLink"
                          {...makeAccessibleClick(() => {
                            openEditUserModal(t.user.id);
                          })}
                          aria-label={`Edit user ${t.user.nameFirst} ${t.user.nameLast}`}
                        >
                          {t.user.nameFirst} {t.user.nameLast}
                        </span>
                      ) : (
                        ""
                      )}
                    </td>

                    {attendeeOrSponsor === "sponsor" && (
                      <td className="tableLink">
                        {t.sponsor && (
                          <span
                            className="iconAction"
                            {...makeAccessibleClick(() => {
                              navigate(`/account/sponsor/${t.sponsorId}/edit`);
                            })}
                          >
                            <img
                              src={editSvg}
                              alt="manage sponsor"
                              className="small"
                            />
                            &nbsp;
                            {t.sponsor?.name}
                          </span>
                        )}
                      </td>
                    )}

                    {attendeeOrSponsor === "sponsor" && (
                      <td>
                        {t.sponsor?.contactNameFirst}{" "}
                        {t.sponsor?.contactNameLast}
                      </td>
                    )}

                    <td className="icon tableLink">
                      <span
                        {...makeAccessibleClick(() => {
                          navigate(`${t.id}/groups`);
                        })}
                        aria-label={`Manage groups (${t.groups.length})`}
                      >
                        <img src={settingSvg} alt="" aria-hidden="true" />
                        &nbsp;
                        {t.groups.length} Group
                        {t.groups.length > 1 ? "s" : null}
                      </span>
                    </td>

                    <td className="icon tableLink">
                      <span
                        {...makeAccessibleClick(async () => {
                          showConfirmToast({
                            title: `Mark Ticket ${t.paid ? "Unpaid" : "Paid"}`,
                            message: `Are you sure you want to mark this ticket ${
                              t.paid ? "unpaid" : "paid"
                            }?`,
                            onConfirm: async () => {
                              t.paid = !t.paid;
                              // datePaid must be null for api to set it when applicable
                              t.datePaid = null;
                              await updateTicket(t);
                              getTicketsResult();
                            },
                          });
                        })}
                        aria-label={
                          t.paid ? "Mark ticket unpaid" : "Mark ticket paid"
                        }
                      >
                        <img src={dollarSvg} alt="" aria-hidden="true" />
                        &nbsp;
                        {t.paid ? "Paid" : "Unpaid"}
                      </span>
                    </td>

                    <td className="icon tableLink">
                      <span
                        {...makeAccessibleClick(async () => {
                          showConfirmToast({
                            title: `Mark Ticket ${t.isCancelled ? "Active" : "Cancelled"}`,
                            message: `Are you sure you want to mark this ticket ${
                              t.isCancelled ? "active" : "cancelled"
                            }?`,
                            btnText: t.isCancelled
                              ? "Activate"
                              : "Mark Cancelled",
                            onConfirm: async () => {
                              t.isCancelled = !t.isCancelled;
                              await updateTicket(t);
                              getTicketsResult();
                            },
                          });
                        })}
                        aria-label={
                          t.isCancelled
                            ? "Mark ticket active"
                            : "Mark ticket cancelled"
                        }
                      >
                        <img
                          src={t.isCancelled ? inactiveSvg : activeSvg}
                          alt=""
                          aria-hidden="true"
                        />
                        &nbsp;
                        {t.isCancelled ? "Cancelled" : "Active"}
                      </span>
                    </td>

                    <td>
                      <button
                        type="button"
                        className="iconBtn"
                        onClick={async () => {
                          showConfirmToast({
                            title: "Delete Ticket",
                            message:
                              "Are you sure you want to delete this ticket?",
                            btnText: "Delete",
                            onConfirm: async () => {
                              await deleteTicket(t.id);
                              notify.success("Ticket deleted successfully.");
                              getTicketsResult();
                            },
                          });
                        }}
                      >
                        <img
                          src={trashSvg}
                          alt="Delete ticket"
                          className="iconAction"
                        />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};
